import React, { useContext, useState } from 'react';
import { Chemical } from '../types/ViewModel/TankCleaning/Chemical';
import { ContextProps } from '../types/ContextProps';
import { Supplier } from '../types/ViewModel/Supplier';
import { Unit } from '../types/ViewModel/Unit';
import { ChemicalCategory } from '../types/ViewModel/TankCleaning/ChemicalCategory';
import { PortCall } from '../types/ViewModel/PortCall';
import { ChemicalRobPortCall } from '../types/ViewModel/TankCleaning/ROB/ChemicalRobPortCall';
import { ChemicalRobLocationDD } from '../types/ViewModel/TankCleaning/ROB/ChemicalRobLocationDD';
import { ChemicalOrderPortCall } from '../types/ViewModel/TankCleaning/Order/ChemicalOrderPortCall';
import { ChemicalOrderUpdateReportData } from '../types/ViewModel/TankCleaning/Order/ChemicalOrderUpdateReportData';
import { ChemicalRobUpdateReportData } from '../types/ViewModel/TankCleaning/ROB/ChemicalRobUpdateReportData';
import { QueryParameters } from '../types/QueryParameters';

type TankCleaningContextType = {
    chemicalList: Chemical[];
    mappedChemicalList: Chemical[] | null;
    chemicalCategoryList: ChemicalCategory[];
    chemicalUnitList: Unit[];
    chemicalSupplierList: Supplier[];
    selectedTankCleaningPortCall: PortCall | null;
    previousTankCleaningPortCall: PortCall | null;
    selectedChemicalRobForPortCall: ChemicalRobPortCall | null;
    selectedChemicalOrderForPortCall: ChemicalOrderPortCall | null;
    selectedChemicalOrderForPortCallList: ChemicalOrderPortCall[] | null;
    chemicalOrderForPortCallList: ChemicalOrderPortCall[] | null;
    chemicalRobForPortCallList: ChemicalRobPortCall[] | null;
    selectedChemicalOrderId: number | null;
    chemicalOrderUpdateReportData: ChemicalOrderUpdateReportData | null;
    chemicalRobUpdateReportData: ChemicalRobUpdateReportData | null;
    robLocationDDList: ChemicalRobLocationDD[];
    isOrderPanelVisible: boolean;
    chemicalReportType: string | null;
    queryParameters: QueryParameters | null;
    isNewOrder: boolean;
    shouldCopyLastRob: boolean | null;
    isSummaryActive: boolean;
    setChemicalList: (chemicals: Chemical[]) => void;
    setMappedChemicalList: (chemicals: Chemical[] | null) => void;
    setChemicalCategoryList: (chemicalCategoryList: ChemicalCategory[]) => void;
    setChemicalUnitList: (units: Unit[]) => void;
    setChemicalSupplierList: (suppliers: Supplier[]) => void;
    setSelectedTankCleaningPortCall: (portCall: PortCall | null) => void;
    setPreviousTankCleaningPortCall: (portCall: PortCall | null) => void;
    setSelectedChemicalRobForPortCall: (chemicalRobPortCall: ChemicalRobPortCall | null) => void;
    setSelectedChemicalOrderForPortCall: (chemicalOrderPortCall: ChemicalOrderPortCall | null) => void;
    setSelectedChemicalOrderForPortCallList: (chemicalOrderPortCallList: ChemicalOrderPortCall[] | null) => void;
    setChemicalOrderForPortCallList: (chemicalOrderPortCallList: ChemicalOrderPortCall[] | null) => void;
    setChemicalRobForPortCallList: (chemicalRobPortCallList: ChemicalRobPortCall[] | null) => void;
    setSelectedChemicalOrderId: (chemicalOrderId: number | null) => void;
    setChemicalOrderUpdateReportData: (chemicalOrderUpdateDataReport: ChemicalOrderUpdateReportData | null) => void;
    setChemicalRobUpdateReportData: (chemicalRobUpdateDataReport: ChemicalRobUpdateReportData | null) => void;
    setRobLocationDDList: (ChemicalRobLocationDDList: ChemicalRobLocationDD[]) => void;
    setIsOrderPanelVisible: (isVisible: boolean) => void;
    setChemicalReportType: (reportType: string | null) => void;
    setQueryParameters: (queryParameters: QueryParameters | null) => void;
    setIsNewOrder: (isNew: boolean) => void;
    setShouldCopyLastRob: (isNew: boolean | null) => void;
    setIsSummaryActive: (isActive: boolean) => void;
};

export const TankCleaningContext = React.createContext<TankCleaningContextType>(undefined!);

export const TankCleaningProvider = ({ children }: ContextProps) => {
    const [chemicalList, setChemicalList] = useState<Chemical[]>([]);
    const [mappedChemicalList, setMappedChemicalList] = useState<Chemical[] | null>(null);
    const [chemicalCategoryList, setChemicalCategoryList] = useState<ChemicalCategory[]>([]);
    const [chemicalUnitList, setChemicalUnitList] = useState<Unit[]>([]);
    const [chemicalSupplierList, setChemicalSupplierList] = useState<Supplier[]>([]);
    const [previousTankCleaningPortCall, setPreviousTankCleaningPortCall] = useState<PortCall | null>(null);
    const [selectedTankCleaningPortCall, setSelectedTankCleaningPortCall] = useState<PortCall | null>(null);
    const [selectedChemicalRobForPortCall, setSelectedChemicalRobForPortCall] = useState<ChemicalRobPortCall | null>(null);
    const [selectedChemicalOrderId, setSelectedChemicalOrderId] = useState<number | null>(null);
    const [selectedChemicalOrderForPortCall, setSelectedChemicalOrderForPortCall] = useState<ChemicalOrderPortCall | null>(null);
    const [selectedChemicalOrderForPortCallList, setSelectedChemicalOrderForPortCallList] = useState<ChemicalOrderPortCall[] | null>(null);
    const [chemicalOrderUpdateReportData, setChemicalOrderUpdateReportData] = useState<ChemicalOrderUpdateReportData | null>(null);
    const [chemicalRobUpdateReportData, setChemicalRobUpdateReportData] = useState<ChemicalRobUpdateReportData | null>(null);
    const [robLocationDDList, setRobLocationDDList] = useState<ChemicalRobLocationDD[]>([]);
    const [isOrderPanelVisible, setIsOrderPanelVisible] = useState<boolean>(false);
    const [chemicalReportType, setChemicalReportType] = useState<string | null>(null);
    const [queryParameters, setQueryParameters] = useState<QueryParameters | null>(null);
    const [isNewOrder, setIsNewOrder] = useState<boolean>(false);
    const [isSummaryActive, setIsSummaryActive] = useState<boolean>(false);
    const [shouldCopyLastRob, setShouldCopyLastRob] = useState<boolean | null>(null);
    const [chemicalOrderForPortCallList, setChemicalOrderForPortCallList] = useState<ChemicalOrderPortCall[] | null>(null);
    const [chemicalRobForPortCallList, setChemicalRobForPortCallList] = useState<ChemicalRobPortCall[] | null>(null);

    return (
        <TankCleaningContext.Provider
            value={{
                chemicalList,
                setChemicalList,
                mappedChemicalList,
                setMappedChemicalList,
                chemicalCategoryList,
                setChemicalCategoryList,
                chemicalUnitList,
                setChemicalUnitList,
                chemicalSupplierList,
                setChemicalSupplierList,
                selectedTankCleaningPortCall,
                setSelectedTankCleaningPortCall,
                robLocationDDList,
                setRobLocationDDList,
                previousTankCleaningPortCall,
                setPreviousTankCleaningPortCall,
                isOrderPanelVisible,
                setIsOrderPanelVisible,
                selectedChemicalRobForPortCall,
                setSelectedChemicalRobForPortCall,
                selectedChemicalOrderForPortCall,
                setSelectedChemicalOrderForPortCall,
                chemicalOrderUpdateReportData,
                setChemicalOrderUpdateReportData,
                chemicalRobUpdateReportData,
                setChemicalRobUpdateReportData,
                selectedChemicalOrderForPortCallList,
                setSelectedChemicalOrderForPortCallList,
                selectedChemicalOrderId,
                setSelectedChemicalOrderId,
                chemicalReportType,
                setChemicalReportType,
                queryParameters,
                setQueryParameters,
                isNewOrder,
                setIsNewOrder,
                isSummaryActive,
                setIsSummaryActive,
                shouldCopyLastRob,
                setShouldCopyLastRob,
                chemicalOrderForPortCallList,
                setChemicalOrderForPortCallList,
                chemicalRobForPortCallList,
                setChemicalRobForPortCallList,
            }}
        >
            {children}
        </TankCleaningContext.Provider>
    );
};

export const useTankCleaningStore = () => useContext(TankCleaningContext);
